import { create } from 'zustand';
import { api } from '@/lib/api-client';
import {
  type ConveraApiLogDto,
  type ConveraApiLogDetailDto,
  type ConveraLogsResponse,
  type DirectionFilter,
  ConveraLogsResponseSchema,
  ConveraApiLogDetailDtoSchema,
} from './types';

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

interface ConveraLogsState {
  logs: ConveraApiLogDto[];
  isLoading: boolean;
  error: string | null;
  total: number;
  outboundCount: number;
  inboundCount: number;
  errorCount: number;
  direction: DirectionFilter;
  page: number;
  limit: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  autoRefresh: boolean;

  fetchLogs: () => Promise<void>;
  setDirection: (direction: DirectionFilter) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSortBy: (field: string, direction: 'asc' | 'desc') => void;
  setAutoRefresh: (enabled: boolean) => void;
  clearError: () => void;
}

// ---------------------------------------------------------------------------
// Build query params from state
// ---------------------------------------------------------------------------

function buildParams(
  direction: DirectionFilter,
  page: number,
  limit: number,
  sortBy: string,
  sortDirection: string,
): Record<string, string> {
  const params: Record<string, string> = {
    page: String(page),
    limit: String(limit),
  };

  if (direction !== 'all') {
    params.direction = direction;
  }

  if (sortBy) {
    params.sortBy = sortBy;
    params.sortDirection = sortDirection;
  }

  return params;
}

// ---------------------------------------------------------------------------
// Zustand store
// ---------------------------------------------------------------------------

export const useConveraLogs = create<ConveraLogsState>((set, get) => ({
  logs: [],
  isLoading: false,
  error: null,
  total: 0,
  outboundCount: 0,
  inboundCount: 0,
  errorCount: 0,
  direction: 'all',
  page: 1,
  limit: 50,
  sortBy: 'created_at',
  sortDirection: 'desc',
  autoRefresh: false,

  fetchLogs: async () => {
    const { direction, page, limit, sortBy, sortDirection } = get();
    set({ isLoading: true, error: null });

    try {
      const params = buildParams(direction, page, limit, sortBy, sortDirection);
      const data = await api.get<ConveraLogsResponse>('/api/convera/logs', {
        params,
      });

      const parsed = ConveraLogsResponseSchema.safeParse(data);
      if (!parsed.success) {
        set({ isLoading: false, error: 'Invalid response format from server.' });
        return;
      }

      set({
        logs: parsed.data.data,
        total: parsed.data.meta.total,
        outboundCount: parsed.data.meta.outbound_count,
        inboundCount: parsed.data.meta.inbound_count,
        errorCount: parsed.data.meta.error_count,
        isLoading: false,
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to load Convera logs.';
      set({ isLoading: false, error: message });
    }
  },

  setDirection: (direction: DirectionFilter) => {
    set({ direction, page: 1 });
  },

  setPage: (page: number) => {
    set({ page });
  },

  setLimit: (limit: number) => {
    set({ limit, page: 1 });
  },

  setSortBy: (field: string, direction: 'asc' | 'desc') => {
    set({ sortBy: field, sortDirection: direction });
  },

  setAutoRefresh: (enabled: boolean) => {
    set({ autoRefresh: enabled });
  },

  clearError: () => {
    set({ error: null });
  },
}));

/**
 * Fetch a single Convera API log entry by id, including sanitised
 * request/response bodies. Kept outside the Zustand store on purpose: the
 * detail row is consumed by the modal alone, so pushing it into the store
 * would force unrelated re-renders of the list. The `signal` argument lets
 * the modal abort an in-flight fetch when the user closes it (see
 * `LogDetailModal`'s cleanup).
 */
export async function fetchLogById(
  id: string,
  signal?: AbortSignal,
): Promise<ConveraApiLogDetailDto> {
  const data = await api.get<unknown>(`/api/convera/logs/${id}`, { signal });
  return ConveraApiLogDetailDtoSchema.parse(data);
}
